/* eslint-disable */
import { BANK_ACCOUNT_TYPE, PAYMENT_METHOD_TYPE } from "./constants";

/**
 * The returned information from ODIN
 * @typedef odinReturnObject
 * @type {object}
 * @property {("SUCCESS"|"FAILURE")} status - response from ODIN.
 */

/**
 * The returned information from Basis Theory
 * @typedef btReturnObject
 * @type {object}
 * @property {string} token - token from BT.
 */

/**
 * Enum for possible Basis Theory card fields
 * @typedef cardField
 * @property ("card") CARD - a one-liner credit card collector containing: cardNumber, expirationDate, cvc.
 */

/**
 * Enum for possible Basis Theory bank fields
 * @typedef bankAccountField
 * @property ("routingNumber") ROUTING_NUMBER - Uses Basis Theory "text" input type - a secure replacement for the <input> tag and enables collecting user string data.
 * @property ("accountNumber") ACCOUNT_NUMBER - Uses Basis Theory "text" input type - a secure replacement for the <input> tag and enables collecting user string data.
 */

/**
 * Enum for possible Odin details fields
 * @typedef detailsField
 * @property ("postalCode") POSTAL_CODE - for collecting an account owners postal code.
 */

/**
 * Card data for Basis Theory - single card element.
 * Note: input instances of the BT elements, not raw data.
 * @typedef btCardData
 * @type {object}
 * @property {import("@basis-theory/web-elements").CardElement} data - BT card element.
 */

/**
 * Bank account data for Basis Theory - split text elements.
 * Note: input instances of the BT elements, not raw data.
 * @typedef btBankAccountData
 * @type {object}
 * @property {import("@basis-theory/web-elements").TextElement} routing_number - routing number element.
 * @property {import("@basis-theory/web-elements").TextElement} account_number - account number element.
 */

/**
 * @typedef {Object} PrevalidationFieldData
 * @property {String} selector - selector for the holder element of the BT field, supplied by the config object
 * @property {(import("@basis-theory/web-elements").TextElement|import("@basis-theory/web-elements").CardElement)} field - the Basis Theory field
 */

/**
 * @typedef {Object<String, PrevalidationFieldData>} BtFields object for btFields used in prevalidation
 */

/**
 * Payload for Basis Theory tokenization
 * @typedef btPayload
 * @type {object}
 * @property {string} [id] - A value or expression specifying the token's ID.
 * @property {paymentType} type - token type, we use bank and card types only.
 * @property {(btCardData | btBankAccountData)} data - user supplied payment data.
 * @property {string} [fingerprint_expression] - Expressions used to fingerprint your token.
 * @property {boolean} [deduplicate_token] - Whether the token is deduplicated on creation.
 */

/**
 * The submit event.
 * 1.  Calls Basis Theory Tokenization.
 * 2.  If no error, calls OdinPay with that token.
 * 3.  Responds with the callback function provided in the OdinPay config.
 * @async
 * @typedef {function} submitEvent
 * @param {BasisTheory} bt - reference to the initialized Basis Theory class.
 * @param {initializedElements} btFields - references to the Basis Theory fields that have been initialized.
 * @param {submitCallback} callback - callback provided in the OdinPay config to return the response to the UI.
 * @return {Promise<void>}
 */

/**
 * Submit button configuration options
 * @typedef {object} submitButtonConfig
 * @property {string} selector - the html buttons id
 * @property {function} callback - callback function to attack to the button's onClick event
 */

/**
 * @typedef {object} btFieldConfig
 * @property {import("@basis-theory/web-elements").ElementStyle} [style] - Object used to customize the element appearance
 * @property {string} [aria-label] - String used to customize the aria-label attribute of the input
 */

/**
 * @typedef {object} billingInfoFieldConfig
 * @property {string} selector - an HTML id to add the input to
 * @property {string} ariaLabel - optional aria label
 * @property {string} [placeholder] - optional placeholder
 * @property {string} [initialValue] - optional initial value
 */

/**
 * Configuration options for a card field.
 * @typedef {object} cardFieldsConfig
 * @property {btFieldConfig} cardInformation - config for the card field
 * @property {billingInfoFieldConfig} postalCode
 * @property {billingInfoFieldConfig} [name]
 * @property {billingInfoFieldConfig} [emailAddress]
 * @property {billingInfoFieldConfig} [phoneNumber]
 * @property {billingInfoFieldConfig} [addressLine1]
 * @property {billingInfoFieldConfig} [addressLine2]
 * @property {billingInfoFieldConfig} [city]
 * @property {billingInfoFieldConfig} [state]
 * @property {billingInfoFieldConfig} [country]
 */

/**
 * Configuration options for a bank field.
 * @typedef {object} bankAccountFieldsConfig
 * @property {btFieldConfig} accountNumber - config for the accountNumber field
 * @property {btFieldConfig} routingNumber - config for the routingNumber field
 * @property {billingInfoFieldConfig} postalCode
 * @property {billingInfoFieldConfig} [name]
 * @property {billingInfoFieldConfig} [emailAddress]
 * @property {billingInfoFieldConfig} [phoneNumber]
 * @property {billingInfoFieldConfig} [addressLine1]
 * @property {billingInfoFieldConfig} [addressLine2]
 * @property {billingInfoFieldConfig} [city]
 * @property {billingInfoFieldConfig} [state]
 * @property {billingInfoFieldConfig} [country]
 */

/**
 * Card element configuration object.
 * We have exposed some of these to the end user.
 * Some elements are not eligible for certain props, see docs for details.
 * https://developers.basistheory.com/docs/sdks/web/javascript/options
 * @typedef {object} btCardConfig
 * @property {submitButtonConfig} submitButton - configuration for the submit button of the form.
 * @property {cardFieldsConfig} fields - fields configuration object for the card form.
 */

/**
 * Bank element configuration object.
 * We have exposed some of these to the end user.
 * Some elements are not eligible for certain props, see docs for details.
 * https://developers.basistheory.com/docs/sdks/web/javascript/options
 * @typedef {object} btBankAccountConfig
 * @property {submitButtonConfig} submitButton - configuration for the submit button of the form.
 * @property {bankAccountFieldsConfig} fields - fields configuration object for the card form.
 */

/**
 * Payload to Odin Payment Method PUT request.
 * @typedef {object} paymentMethodPutPayload
 * @property {string} accountNumber - masked ID from Basis Theory token
 * @property {string} id - fingerprint from Basis Theory token
 * @property {PAYMENT_METHOD_TYPE} type - the type of payment method it is.
 * @property {BANK_ACCOUNT_TYPE} [bankAccountType] - only required if type = BANK_ACCOUNT
 * @property {billingInformation} [billingInformation] - billing information collected from the end user.
 */

/**
 * The Address object for billing info
 * @typedef {object} billingAddress
 * @param {string} [streetAddress1]
 * @param {string} [streetAddress2]
 * @param {string} [city]
 * @param {string} [stateProvince]
 * @param {string} [postalCode]
 * @param {string} [countryCode]
 */
/**
 * The Billing Info object sent to OdinPay
 * @typedef {object} billingInformation
 * @param {string} [name]
 * @param {billingAddress} [address]
 * @param {string} [emailAddress]
 * @param {string} [phoneNumber]
 * @param {string} [bankAccountType] IF bank payment, THEN oneOf(BANK_ACCOUNT_TYPE) ELSE undefined
 */

/**
 * The Billing Info object sent to OdinPay
 * @typedef {object} unparsedBillingInformation
 * @param {string} [name]
 * @param {string} [streetAddress1]
 * @param {string} [streetAddress2]
 * @param {string} [city]
 * @param {string} [stateProvince]
 * @param {string} [postalCode]
 * @param {string} [countryCode]
 * @param {string} [emailAddress]
 * @param {string} [phoneNumber]
 */

/**
 * Response from Odin Payment Method PUT request.
 * @typedef {object} paymentMethodPutResponse
 * @param {string} accountNumber - masked ID from Basis Theory token
 * @param {string} id - fingerprint from Basis Theory token
 * @param {PAYMENT_METHOD_TYPE} type - the type of payment method it is.
 * @param {string} createdAt - time that the token was originally created and stored in OdinPay
 * @param {string} updatedAt - time that the token was updated in OdinPay
 */

/**
 * initialize Basis Theory card form and mount it.
 * @typedef {function} createCardForm
 * @param {btCardConfig} config - to customize the Basis Theory element.
 */

/**
 * initialize Basis Theory bank form and mount it.
 * @typedef {function} createBankAccountForm
 * @param {btBankAccountConfig} config - to customize the Basis Theory element.
 */

/**
 * @typedef {object} odinPayObject
 * @property {createCardForm} createCardForm - function to create + mount a credit card form.
 * @property {createBankAccountForm} createBankAccountForm - function to create + mount a bank account form.
 */

/**
 * @typedef {("backgroundColor"|"color"|"fontFamily"|"fontSize"|"fontSmooth"|"fontStyle"|"fontVariant"|"fontWeight"|"lineHeight"|"letterSpacing"|"textAlign"|"padding"|"textDecoration"|"textShadow"|"textTransform"|"borderWidth"|"borderColor"|"borderStyle"|"borderRadius")} allowedCSSProperty
 */

/**
 * @typedef {({}|Object.<allowedCSSProperty, string>)} styleObject
 */

/**
 * @typedef {object} odinTheme - uses Basis Theory's safe CSS and pseudo-classes
 */

/**
 * Types of components, for now we only use inputs.
 * @type {("input")} elementType
 */

/**
 * Style type.  An internal style is a style that we can insert into the input itself.
 * An external style is a style that we must create and apply to a parent div.
 * @typedef {("internal"|"external)} styleType
 */

/**
 * @typedef {object} inputObject
 * @property {HTMLElement} inputElement
 */

/**
 * @typedef {("visa"|"mastercard"|"american-express"|"discover"|"diners-club"|"jcb"|"unionpay"|"maestro"|"elo"|"hiper"|"hipercard"|"mir"|"unknown")} btCardBrand
 */

/**
 * @typedef {Object} ValidationReturn
 * @property {boolean} isValid
 * @property {Object.<number, string>} errors
 * @property {string[]} [propertyNames]
 */

/**
 * @typedef {Object} ObjectSchema - https://ajv.js.org/json-schema.html#keywords-for-objects
 * @property {"object"} type - the type of the schema
 * @property {string} [title] - information about the data represented by the schema
 * @property {string} [description] - information about the data represented by the schema
 * @property {number} [maxProperties]
 * @property {number} [minProperties]
 * @property {string[]} [required] - should contain all properties with names equal to the elements in the keyword value
 * @property {Object.<string, Object>} [properties] - a map with keys equal to data object properties. Each value in the map should be a JSON Schema.
 * @property {Object.<string, Object>} [patternProperties] - a map with keys equal to regex, and values should be JSON schema
 * @property {(boolean|object)} [additionalProperties] - if false, no additional properties allowed.  If true, all additional properties allowed.  If schema, only additional values matching that schema are allowed.
 * @property {Object.<string, string[]>} [dependantRequired] - a map with keys equal to data object properties.  Values are an array of other object properties that are required if the initial property is present in the data.
 * @property {Object.<string, Object>} [dependantSchemas] - a map with keys equal to data object properties.  Values should be a JSON schema.  If the key is present in the data, the data object must pass the value's schema to pass validation.
 * @property {Object} [propertyNames] - a json schema that validates property names.  IE : propertyNames: { format: "email" }
 * @property {(boolean|Object)} [unevaluatedProperties] - if schema, will be applied to all properties that were not evaluated by other keywords for properties (properties, patternProperties, additionalProperties).  If true, allows unevaluated properties, if false does not allow unevaluated properties.
 * @property {Object} [discriminator] - https://ajv.js.org/json-schema.html#discriminator
 * @property {Object} [not] - value should be JSON schema. Example: not: {minimum: 3} - (2 is valid, 3 is invalid)
 * @property {Object[]} [oneOf] - value should be an array of JSON schemas.  Data is valid if it matches exactly one of the schemas from the array.  Example: oneOf: [{maximum: 3}, {type: "integer"}]
 * @property {Object[]} [anyOf] - value should be an array of JSON schemas.  Data is valid if it matches one or more of the schemas from the array.
 * @property {Object[]} [allOf] - value should be an array of JSON schemas.  Data is valid if it matches all the schemas in the array.
 * @property {Object} [if] - if/then/else
 * @property {Object} [then] - if/then/else
 * @property {Object} [else] - if/then/else - Example:  if: {properties: {foo: {minimum: 10}}}, then: {required: ["bar"]}, else: {required: ["baz"]}
 */

/**
 * @typedef {Object} ApiError
 * @property {"BACKEND_ERROR"} type
 * @property {("UNAUTHORIZED"|"VALIDATION_ERROR")} errorCode
 * @property {String} message
 * @property {String[]} errors
 */
